import { useCallback, useMemo, useState, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  NodeTypes,
  ConnectionMode,
  MarkerType,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useSchemaDesignerStore } from '@/stores/schema-designer-store';
import { TableNodeComponent } from './table-node';
import { AddTableDialog } from './add-table-dialog';
import { SaveCloudDialog } from './save-cloud-dialog';
import { LoadCloudDialog } from './load-cloud-dialog';
import { PublishDialog } from './publish-dialog';
import { SaveStatusIndicator } from './save-status-indicator';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  RiLayoutGridLine,
  RiZoomInLine,
  RiZoomOutLine,
  RiFocusLine,
  RiDeleteBin6Line,
  RiDownloadLine,
  RiUploadLine,
  RiAlertLine,
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
} from '@remixicon/react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const nodeTypes: NodeTypes = {
  tableNode: TableNodeComponent,
};

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: 'hsl(var(--primary))',
    width: 20,
    height: 20,
  },
};

export const SchemaCanvas = () => {
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    exportAsJSON,
    importFromJSON,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useSchemaDesignerStore();

  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const [showResetDialog, setShowResetDialog] = useState(false);

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 300 });
  }, [fitView]);

  const handleZoomIn = useCallback(() => {
    zoomIn({ duration: 300 });
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut({ duration: 300 });
  }, [zoomOut]);

  const handleAutoLayout = useCallback(() => {
    // Simple auto-layout: arrange tables in a grid
    const gridCols = Math.ceil(Math.sqrt(nodes.length));
    const spacing = 400;

    const updatedNodes = nodes.map((node, index) => ({
      ...node,
      position: {
        x: (index % gridCols) * spacing,
        y: Math.floor(index / gridCols) * spacing,
      },
    }));

    useSchemaDesignerStore.getState().setNodes(updatedNodes);
    toast.success('Tables auto-arranged');

    setTimeout(() => {
      fitView({ padding: 0.2, duration: 300 });
    }, 100);
  }, [nodes, fitView]);

  const handleExportJSON = useCallback(() => {
    try {
      const json = exportAsJSON();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `schema-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Schema exported successfully');
    } catch (error) {
      toast.error('Failed to export schema');
      console.error('Export error:', error);
    }
  }, [exportAsJSON]);

  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = event.target?.result as string;
          importFromJSON(json);
          toast.success('Schema imported successfully');
          setTimeout(() => {
            fitView({ padding: 0.2, duration: 300 });
          }, 100);
        } catch (error) {
          toast.error('Failed to import schema');
          console.error('Import error:', error);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, [importFromJSON, fitView]);

  const handleResetSchema = useCallback(() => {
    useSchemaDesignerStore.getState().setNodes([]);
    useSchemaDesignerStore.getState().setEdges([]);
    setShowResetDialog(false);
    toast.success('Schema reset successfully');
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Z or Cmd+Z for Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          undo();
          toast.success('Undone');
        }
      }
      // Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y for Redo
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') || (e.ctrlKey && e.key === 'y')) {
        e.preventDefault();
        if (canRedo) {
          redo();
          toast.success('Redone');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undo, redo]);

  const emptyState = nodes.length === 0;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="relative w-full h-full bg-background">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          connectionMode={ConnectionMode.Loose}
          fitView
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          snapToGrid
          snapGrid={[15, 15]}
          className="bg-muted/30"
        >
        <Background gap={20} size={1} className="bg-muted/20" />

        <Controls
          showZoom={false}
          showFitView={false}
          showInteractive={false}
          className="bg-background border border-border shadow-lg rounded-lg"
        />

        {/* Removed MiniMap - replaced with save status indicator */}

        {/* Top Toolbar */}
        <Panel position="top-left" className="flex items-center gap-2">
          <AddTableDialog />

          {/* Cloud Sync */}
          <div className="flex items-center gap-2">
            <SaveCloudDialog />
            <LoadCloudDialog />
            <PublishDialog />
          </div>

          <div className="flex items-center gap-1 bg-background border border-border rounded-lg shadow-lg p-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={undo}
                  disabled={!canUndo}
                  className="h-8 w-8 p-0"
                >
                  <RiArrowGoBackLine className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Undo (Ctrl+Z)</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={redo}
                  disabled={!canRedo}
                  className="h-8 w-8 p-0"
                >
                  <RiArrowGoForwardLine className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Redo (Ctrl+Shift+Z)</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex items-center gap-1 bg-background border border-border rounded-lg shadow-lg p-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleZoomIn}
                  className="h-8 w-8 p-0"
                >
                  <RiZoomInLine className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Zoom In</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleZoomOut}
                  className="h-8 w-8 p-0"
                >
                  <RiZoomOutLine className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Zoom Out</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleFitView}
                  className="h-8 w-8 p-0"
                >
                  <RiFocusLine className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Fit View</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAutoLayout}
                  className="h-8 w-8 p-0"
                  disabled={nodes.length === 0}
                >
                  <RiLayoutGridLine className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Auto Layout</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="flex items-center gap-1 bg-background border border-border rounded-lg shadow-lg p-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExportJSON}
                  className="h-8 px-3 gap-2"
                  disabled={nodes.length === 0}
                >
                  <RiDownloadLine className="h-4 w-4" />
                  <span className="text-sm">Export</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Export schema as JSON</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleImportJSON}
                  className="h-8 px-3 gap-2"
                >
                  <RiUploadLine className="h-4 w-4" />
                  <span className="text-sm">Import</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Import schema from JSON</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {nodes.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowResetDialog(true)}
                  className="h-8 px-3 gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <RiDeleteBin6Line className="h-4 w-4" />
                  <span className="text-sm">Reset</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Clear all tables and start fresh</p>
              </TooltipContent>
            </Tooltip>
          )}
        </Panel>

        {/* Save Status - Bottom Right */}
        <Panel position="bottom-right">
          <SaveStatusIndicator />
        </Panel>

        {/* Empty State */}
        {emptyState && (
          <Panel position="top-center" className="pointer-events-none">
            <div className="bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg p-8 text-center max-w-md mt-20">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-primary/10 rounded-full">
                  <RiLayoutGridLine className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">Start Designing Your Schema</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create tables, define columns, and establish relationships visually.
                Your schema will auto-generate SQL as you build.
              </p>
              <div className="flex flex-col gap-2 text-xs text-muted-foreground pointer-events-auto">
                <p className="flex items-center gap-2 justify-center">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-muted text-foreground font-semibold">1</span>
                  Click "Add Table" to create your first table
                </p>
                <p className="flex items-center gap-2 justify-center">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-muted text-foreground font-semibold">2</span>
                  Add columns and define their types
                </p>
                <p className="flex items-center gap-2 justify-center">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-muted text-foreground font-semibold">3</span>
                  Drag connections between tables for relationships
                </p>
              </div>
            </div>
          </Panel>
        )}

        {/* Table Count Badge */}
        {!emptyState && (
          <Panel position="top-right">
            <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Tables:</span>
                <span className="font-semibold text-foreground">{nodes.length}</span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">Relationships:</span>
                <span className="font-semibold text-foreground">{edges.length}</span>
              </div>
            </div>
          </Panel>
        )}
      </ReactFlow>

      {/* Reset Schema Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-destructive/10">
                <RiAlertLine className="w-5 h-5 text-destructive" />
              </div>
              Reset Entire Schema?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>This action cannot be undone. This will permanently delete all tables, columns, and relationships from the current schema.</p>
              {nodes.length > 0 && (
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
                  <p className="text-sm font-medium mb-2 text-destructive">You will lose:</p>
                  <ul className="text-sm space-y-1">
                    <li>• {nodes.length} table{nodes.length !== 1 ? 's' : ''}</li>
                    <li>• {nodes.reduce((acc, node) => acc + node.data.columns.length, 0)} total column{nodes.reduce((acc, node) => acc + node.data.columns.length, 0) !== 1 ? 's' : ''}</li>
                    {edges.length > 0 && (
                      <li>• {edges.length} relationship{edges.length !== 1 ? 's' : ''}</li>
                    )}
                  </ul>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetSchema} className="bg-destructive hover:bg-destructive/90">
              Reset Schema
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </TooltipProvider>
  );
};
